import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
  CloudSync as SyncResultIcon,
} from '@mui/icons-material'
import { useInbox, documentUrl } from '@/hooks/useInbox'
import {
  FLIXPECTOR_RATING_COLORS,
  FLIXPECTOR_STATUS_LABELS,
  INBOX_KIND_COLORS,
  INBOX_KIND_LABELS,
  INBOX_STATE_LABELS,
  isPdf,
  isSpreadsheet,
  type InboxItem,
  type InboxItemKind,
  type InboxItemState,
  type SpreadsheetPreview,
} from '@/types/inbox'

function formatAmount(value: number | null, currency: string | null): string {
  if (value == null) return '—'
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(value)
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('de-DE')
}

/** Renders the original document: PDFs inline, spreadsheets as a table. */
function OriginalPreview({
  item,
  loadPreview,
}: {
  item: InboxItem
  loadPreview: (id: string) => Promise<SpreadsheetPreview>
}) {
  const [preview, setPreview] = useState<SpreadsheetPreview | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)

  useEffect(() => {
    setPreview(null)
    setPreviewError(null)
    if (!item.documentId || !isSpreadsheet(item)) return

    loadPreview(item.id)
      .then(setPreview)
      .catch((e) => setPreviewError(e instanceof Error ? e.message : 'Vorschau fehlgeschlagen'))
  }, [item, loadPreview])

  if (!item.documentId) {
    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Kein Anhang — Inhalt der Mail
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {item.bodyPreview || 'Kein Text vorhanden.'}
          </Typography>
        </Paper>
      </Box>
    )
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          {item.filename}
        </Typography>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          href={documentUrl(item.documentId)}
          target="_blank"
          rel="noopener"
        >
          Herunterladen
        </Button>
        {isPdf(item) && (
          <Button
            size="small"
            startIcon={<OpenInNewIcon />}
            href={documentUrl(item.documentId, true)}
            target="_blank"
            rel="noopener"
          >
            In neuem Tab
          </Button>
        )}
      </Stack>

      {isPdf(item) && (
        <Box
          component="iframe"
          src={documentUrl(item.documentId, true)}
          title={item.filename ?? 'Dokument'}
          sx={{ width: '100%', height: 480, border: '1px solid', borderColor: 'divider' }}
        />
      )}

      {isSpreadsheet(item) && (
        <>
          {previewError && <Alert severity="warning">{previewError}</Alert>}
          {!preview && !previewError && <CircularProgress size={20} />}
          {preview && (
            <>
              <Typography variant="caption" color="text.secondary">
                Blatt „{preview.sheetName}"
                {preview.truncated && ` — erste ${preview.rows.length} von ${preview.totalRows} Zeilen`}
              </Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ maxHeight: 420, overflowX: 'auto', mt: 0.5 }}
              >
                <Table size="small" stickyHeader>
                  <TableBody>
                    {preview.rows.map((row, i) => (
                      <TableRow key={i} sx={i === 0 ? { bgcolor: 'action.hover' } : undefined}>
                        {row.map((cell, j) => (
                          <TableCell key={j} sx={{ whiteSpace: 'nowrap' }}>
                            {cell == null ? '' : String(cell)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}

      {!isPdf(item) && !isSpreadsheet(item) && (
        <Alert severity="info">
          Für diesen Dateityp gibt es keine Vorschau — bitte herunterladen.
        </Alert>
      )}
    </Box>
  )
}

export default function InboxPage() {
  const {
    items,
    pending,
    stateFilter,
    setStateFilter,
    loading,
    error,
    refresh,
    accept,
    sendToFlixpector,
    remove,
    dismiss,
    changeKind,
    syncFlixpector,
    loadPreview,
  } = useInbox('new')

  const [selected, setSelected] = useState<InboxItem | null>(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  // Keep the open dialog in sync after an action refreshed the list
  useEffect(() => {
    if (!selected) return
    const fresh = items.find((i) => i.id === selected.id)
    if (fresh) setSelected(fresh)
  }, [items, selected])

  const run = useCallback(
    async (label: string, fn: () => Promise<void>, closeAfter = true) => {
      setBusy(true)
      setActionError(null)
      try {
        await fn()
        setNotice(label)
        if (closeAfter) setSelected(null)
      } catch (e) {
        setActionError(e instanceof Error ? e.message : 'Aktion fehlgeschlagen')
      } finally {
        setBusy(false)
      }
    },
    [],
  )

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Inbox
        </Typography>
        <Chip
          size="small"
          color={pending > 0 ? 'primary' : 'default'}
          label={`${pending} offen`}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Neu laden">
          <IconButton onClick={() => void refresh()} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Per E-Mail eingegangene Rechnungen und Leistungsnachweise. Nichts wird gebucht,
        bevor du entschieden hast.
      </Typography>

      <Tabs
        value={stateFilter}
        onChange={(_, v) => setStateFilter(v as InboxItemState | 'all')}
        sx={{ mb: 1 }}
      >
        <Tab value="new" label="Offen" />
        <Tab value="accepted" label="In der Datenbank" />
        <Tab value="sent_to_flixpector" label="Bei Flixpector" />
        <Tab value="dismissed" label="Erledigt" />
        <Tab value="all" label="Alle" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Art</TableCell>
              <TableCell>Lieferant / Betreff</TableCell>
              <TableCell>Nummer</TableCell>
              <TableCell>Datum</TableCell>
              <TableCell align="right">Betrag</TableCell>
              <TableCell>Eingang</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}

            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Keine Einträge.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {items.map((item) => (
              <TableRow
                key={item.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setActionError(null)
                  setSelected(item)
                }}
              >
                <TableCell>
                  <Chip
                    size="small"
                    color={INBOX_KIND_COLORS[item.kind]}
                    label={INBOX_KIND_LABELS[item.kind]}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item.supplierName || item.subject || '(ohne Betreff)'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.originalSender || item.fromAddress || ''}
                    {item.filename ? ` · ${item.filename}` : ''}
                  </Typography>
                </TableCell>
                <TableCell>{item.invoiceNumber || '—'}</TableCell>
                <TableCell>{formatDate(item.invoiceDate)}</TableCell>
                <TableCell align="right">{formatAmount(item.totalGross, item.currency)}</TableCell>
                <TableCell>{formatDate(item.receivedAt ?? item.createdAt)}</TableCell>
                <TableCell>
                  <Typography variant="caption">{INBOX_STATE_LABELS[item.state]}</Typography>
                  {item.flixpectorRating && (
                    <Chip
                      size="small"
                      sx={{ ml: 0.5 }}
                      color={FLIXPECTOR_RATING_COLORS[item.flixpectorRating] ?? 'default'}
                      label={item.flixpectorRating}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {item.warnings && item.warnings.length > 0 && (
                    <Tooltip title={item.warnings.join(' · ')}>
                      <WarningIcon color="warning" fontSize="small" />
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        maxWidth="lg"
        fullWidth
      >
        {selected && (
          <>
            <DialogTitle>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  color={INBOX_KIND_COLORS[selected.kind]}
                  label={INBOX_KIND_LABELS[selected.kind]}
                />
                <span>{selected.subject || '(ohne Betreff)'}</span>
              </Stack>
            </DialogTitle>

            <DialogContent dividers>
              {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
              {selected.error && <Alert severity="warning" sx={{ mb: 2 }}>{selected.error}</Alert>}

              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Herkunft
                  </Typography>
                  <Typography variant="body2">
                    Von: {selected.fromAddress || '—'}
                    {selected.originalSender && ` · Original: ${selected.originalSender}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Eingang: {formatDate(selected.receivedAt ?? selected.createdAt)}
                  </Typography>
                </Box>

                {selected.kind === 'invoice' && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Erkannte Daten
                    </Typography>
                    <Stack direction="row" spacing={3} flexWrap="wrap">
                      <Typography variant="body2">
                        Lieferant: <strong>{selected.supplierName || '—'}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Nummer: <strong>{selected.invoiceNumber || '—'}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Datum: <strong>{formatDate(selected.invoiceDate)}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Brutto:{' '}
                        <strong>{formatAmount(selected.totalGross, selected.currency)}</strong>
                      </Typography>
                      {selected.confidence && (
                        <Typography variant="body2">
                          Confidence: <strong>{selected.confidence}</strong>
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}

                {selected.warnings && selected.warnings.length > 0 && (
                  <Alert severity="warning">
                    <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                      {selected.warnings.map((w, i) => (
                        <li key={i}>
                          <Typography variant="body2">{w}</Typography>
                        </li>
                      ))}
                    </Stack>
                  </Alert>
                )}

                {selected.flixpectorId && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Flixpector-Prüfung
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                      <Chip
                        size="small"
                        label={
                          FLIXPECTOR_STATUS_LABELS[selected.flixpectorStatus ?? ''] ??
                          selected.flixpectorStatus ??
                          'noch kein Ergebnis'
                        }
                      />
                      {selected.flixpectorRating && (
                        <Chip
                          size="small"
                          color={FLIXPECTOR_RATING_COLORS[selected.flixpectorRating] ?? 'default'}
                          label={`Bewertung: ${selected.flixpectorRating}`}
                        />
                      )}
                      <Button
                        size="small"
                        startIcon={<SyncResultIcon />}
                        disabled={busy}
                        onClick={() =>
                          void run('Ergebnis von Flixpector geholt', () => syncFlixpector(selected.id), false)
                        }
                      >
                        Ergebnis holen
                      </Button>
                    </Stack>

                    {(selected.flixpectorTotalAmount != null ||
                      selected.flixpectorFinalAmount != null) && (
                      <Stack direction="row" spacing={3} sx={{ mt: 1 }} flexWrap="wrap">
                        <Typography variant="body2">
                          Gestellt:{' '}
                          <strong>
                            {formatAmount(selected.flixpectorTotalAmount, selected.currency)}
                          </strong>
                        </Typography>
                        <Typography variant="body2">
                          Abgestimmt:{' '}
                          <strong>
                            {formatAmount(selected.flixpectorFinalAmount, selected.currency)}
                          </strong>
                        </Typography>
                        {selected.flixpectorTotalAmount != null &&
                          selected.flixpectorFinalAmount != null && (
                            <Typography
                              variant="body2"
                              color={
                                selected.flixpectorFinalAmount < selected.flixpectorTotalAmount
                                  ? 'success.main'
                                  : 'text.secondary'
                              }
                            >
                              Differenz:{' '}
                              <strong>
                                {formatAmount(
                                  selected.flixpectorFinalAmount - selected.flixpectorTotalAmount,
                                  selected.currency,
                                )}
                              </strong>
                            </Typography>
                          )}
                      </Stack>
                    )}
                    {selected.flixpectorSyncedAt && (
                      <Typography variant="caption" color="text.secondary">
                        Zuletzt geholt: {new Date(selected.flixpectorSyncedAt).toLocaleString('de-DE')}
                      </Typography>
                    )}
                  </Box>
                )}

                <Divider />

                <OriginalPreview item={selected} loadPreview={loadPreview} />
              </Stack>
            </DialogContent>

            <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, py: 2 }}>
              {selected.state === 'new' && (
                <>
                  <Select
                    size="small"
                    value={selected.kind}
                    disabled={busy}
                    onChange={(e) =>
                      void run(
                        'Belegart geändert',
                        () => changeKind(selected.id, e.target.value as InboxItemKind),
                        false,
                      )
                    }
                    sx={{ minWidth: 190 }}
                  >
                    <MenuItem value="invoice">Rechnung</MenuItem>
                    <MenuItem value="service_description">Leistungsnachweis</MenuItem>
                    <MenuItem value="other">Mail ohne Beleg</MenuItem>
                  </Select>

                  <Box sx={{ flexGrow: 1 }} />

                  <Button
                    color="error"
                    startIcon={<DeleteIcon />}
                    disabled={busy}
                    onClick={() => void run('Eintrag gelöscht', () => remove(selected.id))}
                  >
                    Löschen
                  </Button>

                  {selected.kind === 'other' && (
                    <Button
                      disabled={busy}
                      onClick={() => void run('Als erledigt markiert', () => dismiss(selected.id))}
                    >
                      Erledigt
                    </Button>
                  )}

                  {selected.kind !== 'other' && (
                    <Button
                      variant="outlined"
                      disabled={busy}
                      onClick={() =>
                        void run('An Flixpector übergeben', () => sendToFlixpector(selected.id))
                      }
                    >
                      An Flixpector prüfen
                    </Button>
                  )}

                  {selected.kind === 'invoice' && (
                    <Button
                      variant="contained"
                      disabled={busy}
                      onClick={() =>
                        void run('In die Datenbank übernommen', () => accept(selected.id))
                      }
                    >
                      In die Datenbank
                    </Button>
                  )}
                </>
              )}

              {selected.state !== 'new' && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                    {INBOX_STATE_LABELS[selected.state]}
                    {selected.flixpectorId && ` · Flixpector-ID ${selected.flixpectorId}`}
                  </Typography>
                  {selected.state === 'sent_to_flixpector' && (
                    <Button
                      startIcon={<SyncResultIcon />}
                      disabled={busy}
                      onClick={() =>
                        void run(
                          'Ergebnis geholt — Eintrag wieder offen',
                          () => syncFlixpector(selected.id, true),
                        )
                      }
                    >
                      Ergebnis holen und zurück in die Inbox
                    </Button>
                  )}
                  <Button onClick={() => setSelected(null)}>Schließen</Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  )
}
